import {
  Target,
  Users,
  MapPin,
  Activity,
  Clapperboard,
  MonitorSmartphone,
} from "lucide-react";
import {
  GenderEnum,
  AgeGroupEnum,
  IncomeTierEnum,
  FamilyStatusEnum,
  MaritalStatusEnum,
  MediaConsumptionEnum,
  KidsDemographicsEnum,
  MandatoryIntentEnum,
  InterestSuggestions,
} from "@adlywise/shared";
import { labelForProperty } from "@/features/devices/profile/labels";

const enumOptions = (enumObj, property) =>
  enumObj.options.map((value) => ({
    value,
    label: labelForProperty(property, value),
  }));

const interestOptions = InterestSuggestions.map((value) => ({
  value,
  label: labelForProperty("interest", value),
}));

export const TAG_VALUE_GROUPS = {
  interests: [
    {
      key: "mandatoryIntent",
      label: "Intent",
      options: enumOptions(MandatoryIntentEnum, "mandatoryIntent"),
    },
    {
      key: "interest",
      label: "Interests",
      options: interestOptions,
      freeform: true,
    },
  ],
  demographics: [
    {
      key: "gender",
      label: "Gender",
      options: enumOptions(GenderEnum, "gender"),
    },
    {
      key: "ageGroup",
      label: "Age group",
      options: enumOptions(AgeGroupEnum, "ageGroup"),
    },
    {
      key: "incomeTier",
      label: "Income",
      options: enumOptions(IncomeTierEnum, "incomeTier"),
    },
    {
      key: "familyStatus",
      label: "Family status",
      options: enumOptions(FamilyStatusEnum, "familyStatus"),
    },
    {
      key: "maritalStatus",
      label: "Marital status",
      options: enumOptions(MaritalStatusEnum, "maritalStatus"),
    },
    {
      key: "kidsDemographics",
      label: "Kids",
      options: enumOptions(KidsDemographicsEnum, "kidsDemographics"),
    },
    {
      key: "mediaConsumptionType",
      label: "Media consumption",
      options: enumOptions(MediaConsumptionEnum, "mediaConsumptionType"),
    },
  ],
};

export const DEMOGRAPHIC_VALUES = new Set(
  TAG_VALUE_GROUPS.demographics.flatMap((g) => g.options.map((o) => o.value)),
);

export const CONDITION_CATEGORIES = [
  {
    id: "interests",
    label: "Interests & Intent",
    icon: Target,
    accent: "violet",
    kind: "tags",
    fields: ["tags"],
  },
  {
    id: "demographics",
    label: "Demographics",
    icon: Users,
    accent: "green",
    kind: "tags",
    fields: ["tags"],
  },
  {
    id: "location",
    label: "Location",
    icon: MapPin,
    accent: "blue",
    kind: "fields",
    fields: ["country", "region", "city"],
  },
  {
    id: "behavior",
    label: "Behavior",
    icon: Activity,
    accent: "amber",
    kind: "fields",
    fields: ["sessionDuration", "createdAt"],
  },
  {
    id: "content",
    label: "Content",
    icon: Clapperboard,
    accent: "rose",
    kind: "fields",
    fields: ["categories", "channelId"],
  },
  {
    id: "platform",
    label: "Platform",
    icon: MonitorSmartphone,
    accent: "slate",
    kind: "fields",
    fields: ["platform", "deviceType", "providerUuid"],
  },
];

export const CATEGORY_BY_ID = Object.fromEntries(
  CONDITION_CATEGORIES.map((c) => [c.id, c]),
);

export const ACCENT_CLASS = {
  violet: "bg-(--pastel-violet) text-(--brand-primary)",
  green: "bg-emerald-100 text-emerald-600",
  blue: "bg-sky-100 text-sky-600",
  amber: "bg-amber-100 text-amber-600",
  rose: "bg-rose-100 text-rose-600",
  slate: "bg-slate-100 text-slate-600",
};

export const categoryOfCondition = (condition) => {
  const field = condition.field;
  if (field === "tags") {
    if (condition._cat === "demographics" || condition._cat === "interests") {
      return condition._cat;
    }
    const values = Array.isArray(condition.value)
      ? condition.value
      : [condition.value];
    const allDemographic =
      values.length > 0 && values.every((v) => DEMOGRAPHIC_VALUES.has(v));
    return allDemographic ? "demographics" : "interests";
  }
  const hit = CONDITION_CATEGORIES.find(
    (c) => c.kind === "fields" && c.fields.includes(field),
  );
  return hit ? hit.id : "behavior";
};
